import React, { useState } from "react";
import {
  Bell,
  Sun,
  Moon,
  Globe,
  UserCheck,
  ShoppingCart,
  PlusCircle,
  Menu,
  Sparkles,
  RefreshCw,
  LogOut,
  User
} from "lucide-react";
import { useAuth } from "../../app/context/AuthContext";
import { useTheme } from "../../app/context/ThemeContext";
import { useI18n } from "../../i18n/i18n";
import { useApp } from "../../app/context/AppContext";
import { GlobalProductSearch } from "../search/GlobalProductSearch";
import { useNavigate, Link } from "react-router-dom";

export function Header({ onToggleSidebar }) {
  const { currentUser, availableUsers, switchUser, role } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { lang, changeLanguage, t } = useI18n();
  const { metrics, resetAllData } = useApp();
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-slate-200/80 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-4 sm:px-6 transition-colors">
      {/* Left: Mobile Sidebar Trigger & Global Locator */}
      <div className="flex items-center gap-3 flex-1">
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-xl text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition lg:hidden"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Global Search Bar */}
        <GlobalProductSearch />
      </div>

      {/* Right Controls: Quick POS, Notifications, Role Switcher, Language, Theme, Profile */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Fast Cashier POS Button */}
        <Link
          to="/sales"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 rounded-xl shadow-sm shadow-emerald-600/20 transition-all duration-200"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          <span>POS Counter</span>
        </Link>

        {/* Demo Reset Button */}
        <button
          onClick={() => {
            if (window.confirm("Reset all test data back to clean factory seed state?")) {
              resetAllData();
            }
          }}
          title="Reset Demo Data"
          className="p-2 rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        {/* Notifications Bell */}
        <Link
          to="/notifications"
          className="relative p-2 rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        >
          <Bell className="w-4 h-4" />
          {(metrics.lowStockCount > 0 || metrics.expiringSoonCount > 0) && (
            <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900 animate-pulse" />
          )}
        </Link>

        {/* Language Selector */}
        <div className="relative">
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <Globe className="w-3.5 h-3.5 text-slate-400" />
            <span className="uppercase font-mono text-[11px]">{lang}</span>
          </button>
          {showLangMenu && (
            <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-40 p-1 text-xs">
              <button
                onClick={() => {
                  changeLanguage("en");
                  setShowLangMenu(false);
                }}
                className={`w-full text-left px-3 py-1.5 rounded-lg ${
                  lang === "en" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 font-semibold" : "hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  changeLanguage("hi");
                  setShowLangMenu(false);
                }}
                className={`w-full text-left px-3 py-1.5 rounded-lg ${
                  lang === "hi" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 font-semibold" : "hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                हिंदी (Hindi)
              </button>
              <button
                onClick={() => {
                  changeLanguage("mr");
                  setShowLangMenu(false);
                }}
                className={`w-full text-left px-3 py-1.5 rounded-lg ${
                  lang === "mr" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 font-semibold" : "hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                मराठी (Marathi)
              </button>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          title="Toggle Light / Dark Mode"
          className="p-2 rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        >
          {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-600" />}
        </button>

        <div className="h-5 w-[1px] bg-slate-200 dark:bg-slate-800 hidden sm:block" />

        {/* Role Switcher & Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowRoleMenu(!showRoleMenu)}
            className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <div className={`w-8 h-8 rounded-xl bg-gradient-to-tr ${currentUser?.color || "from-emerald-600 to-teal-500"} text-white flex items-center justify-center font-bold font-display text-xs shadow-sm ring-2 ring-emerald-500/20`}>
              {currentUser?.initials || <User className="w-4 h-4" />}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                {currentUser?.name?.split(" ")[0]}
              </p>
              <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                {role}
              </p>
            </div>
          </button>

          {showRoleMenu && (
            <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl z-40 p-2 text-xs divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-3">
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-tr ${currentUser?.color || "from-emerald-600 to-teal-500"} text-white flex items-center justify-center font-bold text-xs shadow-sm`}>
                    {currentUser?.initials || <User className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-900 dark:text-white font-display">
                      {currentUser?.name}
                    </p>
                    <p className="text-[10px] text-slate-500">{currentUser?.email}</p>
                  </div>
                </div>
                <span className="inline-block mt-2 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  Current Role: {role}
                </span>
              </div>

              <div className="py-2">
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Switch Enterprise Role (Demo RBAC)
                </p>
                {availableUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => {
                      switchUser(user.id);
                      setShowRoleMenu(false);
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl transition text-left ${
                      user.id === currentUser.id
                        ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 font-semibold"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-tr ${user.color || "from-emerald-600 to-teal-500"} text-white flex items-center justify-center font-bold text-[10px] shrink-0`}>
                      {user.initials || <User className="w-3.5 h-3.5" />}
                    </div>
                    <div className="truncate">
                      <p className="text-xs truncate font-medium">{user.name}</p>
                      <p className="text-[10px] text-slate-400">{user.role}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
